/**
 * Environment variable utilities with type safety and fallbacks
 */

// Helper to safely parse numbers from env vars
function parseEnvNumber(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

// Helper to safely parse booleans from env vars
function parseEnvBoolean(value: string | undefined, fallback: boolean): boolean {
  if (!value) return fallback;
  return value.toLowerCase() === 'true';
}

// Calculation constants
export const CALCULATION_CONSTANTS = {
  HOURS_PER_YEAR: parseEnvNumber(process.env.NEXT_PUBLIC_HOURS_PER_YEAR, 2080),
  MINUTES_PER_HOUR: parseEnvNumber(process.env.NEXT_PUBLIC_MINUTES_PER_HOUR, 60),
  WORKFLOW_INCLUDED_REQUESTS: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_INCLUDED_REQUESTS, 1000),
  SMALL_REQUEST_PACKAGE_SIZE: parseEnvNumber(process.env.NEXT_PUBLIC_REQUEST_PACKAGE_SMALL_SIZE, 1000),
  LARGE_REQUEST_PACKAGE_SIZE: parseEnvNumber(process.env.NEXT_PUBLIC_REQUEST_PACKAGE_LARGE_SIZE, 30000),
} as const;

// Default form values
export const DEFAULT_FORM_VALUES = {
  // Workflow type selection (wizard mode) - all true by default for backwards compatibility
  useCredentialRequests: parseEnvBoolean(process.env.NEXT_PUBLIC_DEFAULT_USE_CREDENTIAL_REQUESTS, true),
  useAccessRequests: parseEnvBoolean(process.env.NEXT_PUBLIC_DEFAULT_USE_ACCESS_REQUESTS, true),
  useVisitorRequests: parseEnvBoolean(process.env.NEXT_PUBLIC_DEFAULT_USE_VISITOR_REQUESTS, true),
  
  // Staffing
  cardholderAdmins: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_CARDHOLDER_ADMINS, 2),
  costPerEmployee: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_COST_PER_EMPLOYEE, 75000),
  
  // Visitor Registration
  hasVisitorRegistration: parseEnvBoolean(process.env.NEXT_PUBLIC_DEFAULT_HAS_VISITOR_REGISTRATION, true),
  visitorsPerMonth: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_VISITORS_PER_MONTH, 100),
  timePerVisitor: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_TIME_PER_VISITOR, 15),
  
  // Process Times
  timePerRequest: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_TIME_PER_REQUEST, 10),
  accessNowTimePerRequest: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_ACCESSNOW_TIME_PER_REQUEST, 2),
  accessNowTimePerVisitor: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_ACCESSNOW_TIME_PER_VISITOR, 3),
  
  // Database
  cardholderDatabaseSize: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_CARDHOLDER_DATABASE_SIZE, 1000),
  cardReplacementPercentage: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_CARD_REPLACEMENT_PERCENTAGE, 5),
  staffTurnoverRate: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_STAFF_TURNOVER_RATE, 8),
  staffGrowthRate: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_STAFF_GROWTH_RATE, 5),
  accessRequestsPerCardholder: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_ACCESS_REQUESTS_PER_CARDHOLDER, 3),
  
  // Capacity planning
  extraRequestCapacityPercentage: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_EXTRA_REQUEST_CAPACITY_PERCENTAGE, 10),
  
  // Simple calculator specific inputs (should be 0 by default to not interfere with database calculations)
  credentialRequestsPerMonth: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_CREDENTIAL_REQUESTS_PER_MONTH, 0),
  accessRequestsPerMonth: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_ACCESS_REQUESTS_PER_MONTH, 0),
  
  // Workflow
  differentiateEmployeeContractor: parseEnvBoolean(process.env.NEXT_PUBLIC_DEFAULT_DIFFERENTIATE_EMPLOYEE_CONTRACTOR, false),
  
  // Risk Mitigation Value (operational insurance)
  operationalInsuranceValue: parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_OPERATIONAL_INSURANCE_VALUE, 0),
  
  // Industry and location
  industry: undefined, // Start with no industry selected, use country average
  country: process.env.NEXT_PUBLIC_DEFAULT_COUNTRY || 'default',
} as const;

// Default pricing configuration (currency-specific)
export const DEFAULT_PRICING_CONFIG = {
  NZD: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_NZD, 4000),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_NZD, 500),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_NZD, 10000),
  },
  USD: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_USD, 2500),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_USD, 300),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_USD, 8000),
  },
  AUD: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_AUD, 3600),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_AUD, 450),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_AUD, 10000),
  },
  GBP: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_GBP, 2000),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_GBP, 250),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_GBP, 6500),
  },
  EUR: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_EUR, 2300),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_EUR, 280),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_EUR, 7300),
  },
  ZAR: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_ZAR, 43000),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_ZAR, 5400),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_ZAR, 140000),
  },
  CAD: {
    workflowPrice: parseEnvNumber(process.env.NEXT_PUBLIC_WORKFLOW_PRICE_CAD, 3200),
    extraRequestsSmallPrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_SMALL_PRICE_CAD, 400),
    extraRequestsLargePrice: parseEnvNumber(process.env.NEXT_PUBLIC_EXTRA_REQUESTS_LARGE_PRICE_CAD, 10500),
  },
} as const;

// Authentication configuration
export const AUTH_CONFIG = {
  // Client-side: which auth method to use (safe to expose)
  method: process.env.NEXT_PUBLIC_AUTH_METHOD as 'password' | 'email' | undefined,
  
  // Server-side only: password for password-based auth (NOT exposed to client)
  password: process.env.ACCESS_PASSWORD || '',
  
  // Server-side only: allowed email domains for magic link auth (comma-delimited)
  emailDomains: process.env.AUTH_EMAIL_DOMAINS || 'gallagher.com',
  
  // Server-side only: session expiry in minutes (default 180 = 3 hours)
  expiryMinutes: parseEnvNumber(process.env.AUTH_EXPIRY, 180),
} as const;

// Email service configuration (server-side only)
export const EMAIL_CONFIG = {
  service: process.env.EMAIL_SERVICE as 'sendgrid' | 'resend' | undefined,
  from: process.env.EMAIL_FROM || process.env.RESEND_FROM_EMAIL || '',
  sendgridApiKey: process.env.SENDGRID_API_KEY || '',
  resendApiKey: process.env.RESEND_API_KEY || '',
  
  // URLs for email templates
  calculatorUrl: process.env.EMAIL_CALCULATOR_URL || '',
  companyUrl: process.env.EMAIL_COMPANY_URL || '',
} as const;

// Feature flags
export const FEATURE_FLAGS = {
  hideAboutPage: parseEnvBoolean(process.env.NEXT_PUBLIC_HIDE_ABOUT_PAGE, false),
  hideDetailedCalculator: parseEnvBoolean(process.env.NEXT_PUBLIC_HIDE_DETAILED_CALCULATOR, false),
} as const;

// Currency configuration (fixed regional pricing, no exchange rates)
export const CURRENCY_CONFIG = {
  currencies: {
    NZD: {
      symbol: '$',
      decimals: 0,
      name: 'New Zealand Dollar'
    },
    AUD: {
      symbol: '$',
      decimals: 0,
      name: 'Australian Dollar'
    },
    USD: {
      symbol: '$',
      decimals: 0,
      name: 'US Dollar'
    },
    GBP: {
      symbol: '£',
      decimals: 0,
      name: 'British Pound'
    },
    EUR: {
      symbol: '€',
      decimals: 0,
      name: 'Euro'
    },
    ZAR: {
      symbol: 'R',
      decimals: 0,
      name: 'South African Rand'
    },
    CAD: {
      symbol: '$',
      decimals: 0,
      name: 'Canadian Dollar'
    }
  }
} as const;

// Currency utility types
export type SupportedCurrency = keyof typeof CURRENCY_CONFIG.currencies;
export type CurrencyInfo = typeof CURRENCY_CONFIG.currencies[SupportedCurrency];